import React, { useMemo, useState } from 'react';
import { useStore, useNav } from '../store/StoreContext';
import {
    format, parseISO, addDays, addMonths,
    startOfMonth, endOfMonth, startOfWeek, endOfWeek,
    isSameMonth, isToday,
} from 'date-fns';
import { IconUsers, IconTruck, IconPlus } from '../components/icons';
import { computeCapacityData, loadTone, dayTone } from '../lib/capacity';
import { formatTime12 } from '../utils/formatters';
import { getEstimateStatus } from '../utils';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../hooks/useToast';
import { Button, PageHeader, Toolbar, PeriodStepper, pageWrapCls, ChoiceKeys, Input, FormField } from '../components/ui';
import { moveStatusMeta, jobTypeShort, fmtMoveDate } from '../lib/moves';

// CapacityPage — booking-side "do we have room this day?" view.
//
// Month heatmap; each day shows CREW and TRUCK load vs available
// (Jaylen 2026-07-29, for Susie/Taylor's booking workflow). Shared math
// lives in src/lib/capacity.js (also feeds the Dashboard CapacityStrip).
//
// HARD load (colors the day) = booked jobs at PLANNED numbers — booking
// happens weeks before dispatch assigns crews, so assigned-only would
// make every future day look empty. Multi-day jobs occupy each day.
// SOFT signals (markers, not load): quoted estimates circling the day,
// tentative-date clients aimed at it, walkthrough appointments.
//
// This is a booking heuristic, not a scheduler: one truck CAN run two
// small jobs in a day, so "over capacity" means "look closely", not
// "impossible". The dispatch board stays the source of truth.
//
// Moves (2026-09-18): soft signals read the MOVES (quoting/quoted =
// Quote Pending, lead/walkthrough = Interested, any tentative date = ≈ with
// a "~" chip). Supply honours day_flags (Closed = zero supply, struck;
// Full = a lead said stop selling — hatched stripe) and truck_downtime
// (a truck out for brakes is not in the day's truck count). The day panel
// owns the Open / Full / Closed toggle (store.saveDayFlag); every
// DatePicker in the app shades from the same rows.

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Hatched header stripe for a Full day (same idea as the DatePicker's shading).
const FULL_STRIPE = 'repeating-linear-gradient(135deg, #fbbf24 0 4px, #fde68a 4px 8px)';

function CapacityPage() {
    const { jobs, crew, trucks, estimates, clients, availability, moves, dayFlags, truckDowntime, saveDayFlag } = useStore();
    const { navigateToNewClient, navigateToClient } = useNav();
    const { user, displayName } = useAuth();
    const { show: showToast } = useToast();
    const [month, setMonth] = useState(() => startOfMonth(new Date()));
    const [selectedIso, setSelectedIso] = useState(null);
    // Draft note for the day-flag toggle (only sent with Full / Closed).
    const [flagNote, setFlagNote] = useState('');
    const [flagSaving, setFlagSaving] = useState(false);

    const cap = useMemo(
        () => computeCapacityData({ jobs, estimates, clients, crew, trucks, availability, moves, dayFlags, truckDowntime }),
        [jobs, estimates, clients, crew, trucks, availability, moves, dayFlags, truckDowntime],
    );
    const { activeCrewCount, activeTruckCount, loadByDate, offByDate, softByDate, tentativeByDate, walkthroughsByDate, interestByDate, flagsByDate, supplyFor, downtimeItemsByDate } = cap;

    const byName = displayName || null;
    const setFlag = async (date, kind) => {
        setFlagSaving(true);
        try {
            await saveDayFlag(date, kind, kind ? (flagNote.trim() || null) : null, byName);
            showToast(kind === 'closed' ? `${format(parseISO(date), 'EEE MMM d')} marked closed` : kind === 'full' ? `${format(parseISO(date), 'EEE MMM d')} marked full — no more jobs` : `${format(parseISO(date), 'EEE MMM d')} is open again`);
        } catch (e) {
            showToast('Error: ' + (e?.message || e), 'error');
        } finally { setFlagSaving(false); }
    };

    const clientById = useMemo(() => {
        const m = new Map();
        for (const c of clients || []) m.set(c.id, c);
        return m;
    }, [clients]);

    const gridDays = useMemo(() => {
        const out = [];
        let d = startOfWeek(startOfMonth(month));
        const end = endOfWeek(endOfMonth(month));
        while (d <= end) { out.push(d); d = addDays(d, 1); }
        return out;
    }, [month]);

    const selected = selectedIso ? {
        iso: selectedIso,
        load: loadByDate.get(selectedIso) || { crewNeeded: 0, truckNeeded: 0, laborHours: 0, jobs: [] },
        off: offByDate.get(selectedIso) || { count: 0, names: [] },
        soft: softByDate.get(selectedIso) || { count: 0, items: [] },
        tentative: tentativeByDate.get(selectedIso) || { count: 0, names: [], items: [] },
        walkthroughs: walkthroughsByDate.get(selectedIso) || { count: 0, items: [] },
        interest: interestByDate.get(selectedIso) || { count: 0, items: [] },
        flag: flagsByDate.get(selectedIso) || null,
        supply: supplyFor(selectedIso),
        downtime: downtimeItemsByDate.get(selectedIso) || [],
    } : null;

    // Live counts for the header: supply (active crew / trucks) and the
    // number of booked jobs that touch this month (multi-day jobs count once).
    const monthJobCount = useMemo(() => {
        const prefix = format(month, 'yyyy-MM');
        const ids = new Set();
        for (const [iso, load] of loadByDate) {
            if (!iso.startsWith(prefix)) continue;
            for (const { job } of (load?.jobs || [])) ids.add(job.id);
        }
        return ids.size;
    }, [loadByDate, month]);
    const monthLabel = format(month, 'MMMM yyyy');
    const summary = `${activeCrewCount} active crew · ${activeTruckCount} active ${activeTruckCount === 1 ? 'truck' : 'trucks'} · ${monthJobCount} ${monthJobCount === 1 ? 'job' : 'jobs'} booked in ${format(month, 'MMMM')}`;

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Capacity"
                summary={summary}
                helpTitle="How to read this page"
                help={<>
                    <p>Each day shows <span className="font-semibold">crew and truck load</span> — planned demand from booked jobs vs what's available. <span className="text-emerald-600 font-semibold">Green</span> = open, <span className="text-amber-600 font-semibold">amber</span> = filling up, <span className="text-rose-600 font-semibold">red</span> = at/over capacity — look closely before booking.</p>
                    <div className="space-y-1.5 pt-1">
                        <p className="flex items-center gap-2"><span className="text-[10px] font-bold text-violet-700 bg-violet-100 border border-dashed border-violet-300 rounded px-1 flex-shrink-0">Quote Pending</span><span>quoted for this day, client hasn't booked yet</span></p>
                        <p className="flex items-center gap-2"><span className="text-[10px] font-bold text-sky-700 bg-sky-100 rounded px-1 flex-shrink-0">Interested</span><span>intake picked this date, no quote yet</span></p>
                        <p className="flex items-center gap-2"><span className="text-[10px] font-bold text-amber-700 bg-amber-100 rounded px-1 flex-shrink-0">~ Tentative</span><span>a move date not confirmed with the client yet (still a real date)</span></p>
                        <p className="flex items-center gap-2"><span className="text-[10px] font-bold text-indigo-700 bg-indigo-100 rounded px-1 flex-shrink-0">Walkthrough</span><span>estimator visit — doesn't consume crew or trucks</span></p>
                        <p className="flex items-center gap-2"><span className="text-[10px] font-bold text-orange-700 bg-orange-100 rounded px-1 flex-shrink-0">Crew Off</span><span>requested off that day — reduces available crew</span></p>
                        <p className="flex items-center gap-2"><span className="text-[10px] font-bold text-amber-900 rounded px-1 flex-shrink-0" style={{ background: FULL_STRIPE }}>Full</span><span>a lead said stop selling this day — hatched stripe, pickers warn</span></p>
                        <p className="flex items-center gap-2"><span className="text-[10px] font-bold text-slate-500 bg-slate-200 rounded px-1 flex-shrink-0 line-through">Closed</span><span>holiday / break — zero crew and trucks, pickers refuse it</span></p>
                    </div>
                    <p className="text-xs text-slate-500 pt-1">Trucks marked out of service on the Fleet page drop out of that day's truck count. Click a day and use Open / Full / Closed at the bottom of its panel to flag it.</p>
                    <p className="text-xs text-slate-400 pt-1">This is a booking heuristic — numbers are planned until dispatch assigns, and one truck can run multiple small jobs. Red means "check the dispatch board", not "impossible". Hover a day for estimated labor-hours.</p>
                </>}
            />

            {/* Month stepper */}
            <Toolbar>
                <PeriodStepper
                    label={monthLabel}
                    onPrev={() => setMonth(m => addMonths(m, -1))}
                    onNext={() => setMonth(m => addMonths(m, 1))}
                    onToday={() => setMonth(startOfMonth(new Date()))}
                />
            </Toolbar>

            {/* Month grid */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
                    {DOW.map(d => <div key={d} className="px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">{d}</div>)}
                </div>
                <div className="grid grid-cols-7">
                    {gridDays.map(day => {
                        const dayIso = format(day, 'yyyy-MM-dd');
                        const inMonth = isSameMonth(day, month);
                        const load = loadByDate.get(dayIso);
                        const off = offByDate.get(dayIso);
                        const soft = softByDate.get(dayIso);
                        const tentative = tentativeByDate.get(dayIso);
                        const wt = walkthroughsByDate.get(dayIso);
                        const interest = interestByDate.get(dayIso);
                        // Supply honours Closed days (zero) and trucks out of service (2026-09-18).
                        const { crewAvail, truckAvail, closed, full } = supplyFor(dayIso);
                        const crewNeeded = load?.crewNeeded || 0;
                        const truckNeeded = load?.truckNeeded || 0;
                        const tone = closed ? 'bg-slate-100 hover:bg-slate-100' : dayTone(crewNeeded, crewAvail, truckNeeded, truckAvail);
                        const crewTone = loadTone(crewNeeded, crewAvail);
                        const truckTone = loadTone(truckNeeded, truckAvail);
                        const isSelected = selectedIso === dayIso;
                        const hasAnything = crewNeeded > 0 || truckNeeded > 0 || (off?.count || 0) > 0 || soft || tentative || wt || interest;
                        const cellFlagNote = flagsByDate.get(dayIso)?.note;
                        return (
                            <button
                                key={dayIso}
                                onClick={() => { setSelectedIso(isSelected ? null : dayIso); setFlagNote(''); }}
                                title={closed ? `Closed${cellFlagNote ? ` — ${cellFlagNote}` : ''}` : full ? `Full — no more jobs${cellFlagNote ? ` — ${cellFlagNote}` : ''}` : (load?.laborHours ? `≈ ${load.laborHours}h estimated labor` : undefined)}
                                className={`relative min-h-[96px] p-1.5 border-b border-r border-slate-100 text-left flex flex-col transition-colors ${inMonth ? tone : 'bg-slate-50/70'} ${isSelected ? 'ring-2 ring-inset ring-red-400' : ''}`}
                            >
                                {/* Full = hatched stripe along the top edge (the DatePicker hatches the same way). */}
                                {inMonth && full && !closed && <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1.5" style={{ background: FULL_STRIPE }} />}
                                <div className={`flex items-center justify-between ${inMonth && full && !closed ? 'mt-1' : ''}`}>
                                    <span className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${isToday(day) ? 'bg-red-600 text-white' : inMonth ? (closed ? 'text-slate-400 line-through' : 'text-slate-700') : 'text-slate-300'}`}>
                                        {format(day, 'd')}
                                    </span>
                                    {inMonth && closed ? (
                                        <span className="text-[9px] font-extrabold uppercase tracking-wide text-slate-500 bg-slate-200 rounded px-1 py-0.5 leading-none">Closed</span>
                                    ) : inMonth && full ? (
                                        <span className="text-[9px] font-extrabold uppercase tracking-wide text-amber-900 rounded px-1 py-0.5 leading-none" style={{ background: FULL_STRIPE }}>Full</span>
                                    ) : load?.jobs?.length > 0 && (
                                        <span className="text-[10px] font-semibold text-slate-400">{load.jobs.length} job{load.jobs.length > 1 ? 's' : ''}</span>
                                    )}
                                </div>
                                {inMonth && (crewNeeded > 0 || truckNeeded > 0) && (
                                    <div className={`mt-1.5 space-y-1 ${closed ? 'opacity-60' : ''}`}>
                                        <div className="flex items-center gap-1">
                                            <IconUsers size={11} className="text-slate-400 flex-shrink-0"/>
                                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                <div className={`h-full rounded-full ${crewTone.bar}`} style={{ width: `${Math.min(100, crewAvail > 0 ? (crewNeeded / crewAvail) * 100 : 100)}%` }}/>
                                            </div>
                                            <span className="text-[10px] tabular-nums text-slate-500 font-medium">{crewNeeded}/{crewAvail}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <IconTruck size={11} className="text-slate-400 flex-shrink-0"/>
                                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                <div className={`h-full rounded-full ${truckTone.bar}`} style={{ width: `${Math.min(100, truckAvail > 0 ? (truckNeeded / truckAvail) * 100 : 100)}%` }}/>
                                            </div>
                                            <span className="text-[10px] tabular-nums text-slate-500 font-medium">{truckNeeded}/{truckAvail}</span>
                                        </div>
                                    </div>
                                )}
                                {/* Soft-signal chips — pinned BOTTOM-LEFT of the cell
                                    (mt-auto in the flex column), tinted backgrounds,
                                    capitalized labels (Jaylen). */}
                                {inMonth && (soft || tentative || wt || interest || (off?.count || 0) > 0) && (
                                    <div className="mt-auto pt-1 flex items-end gap-1 flex-wrap justify-start">
                                        {soft && (
                                            <span className="text-[9px] font-bold text-violet-700 bg-violet-100 border border-dashed border-violet-300 rounded px-1 py-0.5 leading-none" title={soft.items.map(s => s.client?.name || '?').join(', ')}>
                                                {soft.count} Quote{soft.count > 1 ? 's' : ''} Pending
                                            </span>
                                        )}
                                        {interest && (
                                            <span className="text-[9px] font-bold text-sky-700 bg-sky-100 rounded px-1 py-0.5 leading-none" title={interest.items.map(i => i.client?.name || '?').join(', ')}>
                                                {interest.count} Interested
                                            </span>
                                        )}
                                        {tentative && (
                                            <span className="text-[9px] font-bold text-amber-700 bg-amber-100 border border-dashed border-amber-400 rounded px-1 py-0.5 leading-none" title={tentative.names.join(', ')}>~ {tentative.count} Tentative</span>
                                        )}
                                        {wt && (
                                            <span className="text-[9px] font-bold text-indigo-700 bg-indigo-100 rounded px-1 py-0.5 leading-none" title={wt.items.map(w => w.client?.name || '?').join(', ')}>{wt.count} Walkthrough{wt.count > 1 ? 's' : ''}</span>
                                        )}
                                        {(off?.count || 0) > 0 && (
                                            <span className="text-[9px] font-bold text-orange-700 bg-orange-100 rounded px-1 py-0.5 leading-none" title={off.names.join(', ')}>{off.count} Crew Off</span>
                                        )}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Day detail */}
            {selected && (
                <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <h2 className={`font-bold text-slate-900 flex items-center gap-2 ${selected.supply.closed ? 'line-through text-slate-500' : ''}`}>
                            {format(parseISO(selected.iso), 'EEEE, MMMM d')}
                            {selected.supply.closed && <span className="no-underline text-[10px] font-extrabold uppercase tracking-wide text-slate-500 bg-slate-200 rounded px-1.5 py-0.5">Closed</span>}
                            {selected.supply.full && !selected.supply.closed && <span className="text-[10px] font-extrabold uppercase tracking-wide text-amber-900 rounded px-1.5 py-0.5" style={{ background: FULL_STRIPE }}>Full</span>}
                        </h2>
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-500 tabular-nums">
                                crew {selected.load.crewNeeded}/{selected.supply.crewAvail} · trucks {selected.load.truckNeeded}/{selected.supply.truckAvail}
                                {selected.load.laborHours > 0 && <> · ≈{selected.load.laborHours}h labor</>}
                            </span>
                            <Button
                                variant="primary"
                                size="sm"
                                leftIcon={<IconPlus size={13}/>}
                                onClick={() => navigateToNewClient(selected.iso)}
                            >
                                Book this day
                            </Button>
                        </div>
                    </div>

                    {selected.flag?.note && (
                        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                            <span className="font-bold">{selected.supply.closed ? 'Closed' : 'Full'}:</span> {selected.flag.note}
                        </div>
                    )}

                    {selected.off.count > 0 && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                            Off that day: {selected.off.names.length ? selected.off.names.join(', ') : `${selected.off.count} crew`}
                        </div>
                    )}

                    {/* Trucks out of service (truck_downtime, Fleet page) — not in the truck count above. */}
                    {selected.downtime.length > 0 && (
                        <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                            <span className="font-bold">Trucks out:</span>{' '}
                            {selected.downtime.map(({ row, truck }, i) => (
                                <span key={row.id || i}>{i > 0 ? ', ' : ''}{truck?.name || 'Truck'}{row.reason ? ` — ${row.reason}` : ''}</span>
                            ))}
                        </div>
                    )}

                    {selected.load.jobs.length === 0 ? (
                        <p className="text-sm text-slate-400">No jobs scheduled — wide open.</p>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {selected.load.jobs.map(({ job, crewFor, trucksFor, spanDay, spanTotal }) => {
                                const client = clientById.get(job.client_id);
                                const assignedCrew = Array.isArray(job.crew_ids) ? job.crew_ids.length : 0;
                                return (
                                    <div key={job.id + (spanDay || '')} className="py-2 flex items-center justify-between gap-3 text-sm">
                                        <div className="min-w-0">
                                            <span className="font-medium text-slate-800">{client?.name || 'Unknown client'}</span>
                                            <span className="text-slate-400 text-xs ml-2">
                                                {job.job_label || job.type}
                                                {spanDay ? ` · day ${spanDay}/${spanTotal}` : (job.time ? ` · ${formatTime12(job.time)}` : '')}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-slate-500 flex-shrink-0 tabular-nums">
                                            <span className="flex items-center gap-1"><IconUsers size={12}/>{crewFor}{assignedCrew === 0 && crewFor > 0 ? ' planned' : ''}</span>
                                            <span className="flex items-center gap-1"><IconTruck size={12}/>{trucksFor}</span>
                                            <span className={`px-2 py-0.5 rounded-full font-semibold uppercase text-[10px] ${job.status === 'completed' ? 'bg-slate-100 text-slate-500' : job.status === 'unassigned' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{job.status}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Soft demand: quotes circling this day */}
                    {selected.soft.count > 0 && (
                        <div className="border border-dashed border-violet-300 bg-violet-50/40 rounded-lg px-3 py-2">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700 mb-1">Quotes circling this day (not booked)</div>
                            <div className="space-y-0.5">
                                {/* Items are { move, estimate (first live option — null while quoting), client }. */}
                                {selected.soft.items.map(({ estimate, move, client }) => (
                                    <div key={move?.id || estimate?.id} className="flex items-center justify-between text-xs">
                                        <button className="font-medium text-slate-700 hover:text-violet-700 hover:underline text-left" onClick={() => client?.id && navigateToClient(client.id)}>
                                            {client?.name || 'Unknown'}{move?.load_tentative ? <span className="text-amber-700"> ~</span> : ''}
                                        </button>
                                        <span className="text-slate-500 tabular-nums">
                                            {move?.job_type ? jobTypeShort(move.job_type) : ''}
                                            {estimate?.estimated_hours ? ` · ${estimate.estimated_hours}h` : ''}
                                            {estimate?.crew_size_needed ? ` · ${estimate.crew_size_needed} crew` : ''}
                                            <span className="ml-2 uppercase text-[9px] font-bold text-violet-600">{estimate ? getEstimateStatus(estimate.status).label : moveStatusMeta(move?.status).label}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Interested: intake picked this date, no quote/booking yet */}
                    {selected.interest.count > 0 && (
                        <div className="border border-sky-200 bg-sky-50/50 rounded-lg px-3 py-2">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700 mb-1">Interested in this day (from intake — not quoted yet)</div>
                            <div className="space-y-0.5">
                                {/* Items are { client, move } — the move carries type / time / tentative (2026-09-18). */}
                                {selected.interest.items.map(({ client, move }) => (
                                    <div key={move?.id || client.id} className="flex items-center justify-between text-xs">
                                        <button className="font-medium text-slate-700 hover:text-sky-700 hover:underline" onClick={() => navigateToClient(client.id)}>
                                            {client.name || 'Unknown'}{move?.load_tentative ? <span className="text-amber-700"> ~</span> : ''}
                                        </button>
                                        <span className="text-slate-400">
                                            {jobTypeShort(move?.job_type || client.job_type)}
                                            {(move?.load_time || client.move_time) ? ` · ${formatTime12(move?.load_time || client.move_time)}` : ''}
                                            {move?.status === 'walkthrough' ? ' · walkthrough' : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Tentative clients aimed here */}
                    {selected.tentative.count > 0 && (
                        <div className="text-xs text-amber-800 bg-amber-50/70 border border-dashed border-amber-300 rounded-lg px-3 py-2">
                            <span className="font-bold">~ Tentative:</span>{' '}
                            {(selected.tentative.items || []).length > 0 ? selected.tentative.items.map(({ move, client, key, label }, i) => (
                                <span key={(move?.id || client?.id || i) + key}>
                                    {i > 0 ? ', ' : ''}
                                    {client?.id ? <button className="font-medium hover:underline" onClick={() => navigateToClient(client.id)}>{client.name || 'Unknown'}</button> : (client?.name || 'Unknown')}
                                    {move && label ? <span className="text-amber-700"> ({label.toLowerCase()} {fmtMoveDate(key === 'pack' ? move.pack_date : key === 'unload' ? move.unload_date : move.load_date, { tentative: true })})</span> : ''}
                                </span>
                            )) : selected.tentative.names.join(', ')}
                            <span className="text-amber-700"> — a real date, not confirmed with the client yet. Confirm it on the estimate or in the client file.</span>
                        </div>
                    )}

                    {/* Walkthroughs */}
                    {selected.walkthroughs.count > 0 && (
                        <div className="text-xs text-indigo-800 bg-indigo-50/60 border border-indigo-100 rounded-lg px-3 py-2">
                            <span className="font-bold">Walkthroughs:</span>{' '}
                            {selected.walkthroughs.items.map(({ job, client }, i) => (
                                <span key={job.id}>{i > 0 ? ', ' : ''}{client?.name || '?'}{job.time ? ` (${formatTime12(job.time)})` : ''}</span>
                            ))}
                            <span className="text-indigo-500"> — estimator time, doesn't consume crew/trucks.</span>
                        </div>
                    )}

                    {/* Day flag (day_flags, 2026-09-18): Open / Full / Closed.
                        Full = "no more jobs" (the old hand-typed BOOKED
                        marker) — the day keeps its computed room, pickers
                        warn. Closed = holiday / break — zero supply,
                        pickers refuse. Anyone who books can set it; the
                        note says why ("Labor Day", "Jaylen: hold for the
                        Hendersons"). */}
                    <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-end gap-3 flex-wrap">
                            <FormField label="This day is">
                                <ChoiceKeys
                                    size="sm"
                                    ariaLabel="Day status"
                                    disabled={flagSaving}
                                    items={[
                                        { value: 'open', label: 'Open' },
                                        { value: 'full', label: 'Full — no more jobs', color: '#b45309' },
                                        { value: 'closed', label: 'Closed', color: '#475569' },
                                    ]}
                                    value={selected.flag?.kind || 'open'}
                                    onChange={(v) => setFlag(selected.iso, v === 'open' ? null : v)}
                                />
                            </FormField>
                            <FormField label="Why (optional)" className="flex-1 min-w-[200px]">
                                <Input
                                    value={flagNote}
                                    onChange={(e) => setFlagNote(e.target.value)}
                                    placeholder={selected.flag?.note || 'e.g. Labor Day · holding for a big job'}
                                    onBlur={() => { if (selected.flag?.kind && flagNote.trim() && flagNote.trim() !== (selected.flag.note || '')) setFlag(selected.iso, selected.flag.kind); }}
                                />
                            </FormField>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1.5">Full keeps the numbers but tells the pickers to warn; Closed zeroes crew and trucks and pickers won't offer the day.</p>
                    </div>

                </div>
            )}
        </div>
    );
}

export default CapacityPage;
